import { useEffect, useState } from "react"

import { AdminTokenService } from "services/token/AdminTokenService"

/** Shown until the tenant's config arrives, and if it cannot be read at all. */
const FALLBACK_SYMBOL = "TOKEN"

/**
 * Cached per tenant for the life of the page.
 *
 * The ticker is a label on screens that already make their own calls, so
 * without this every tab that prints it would issue its own config request on
 * mount. It is display-only data that changes about never.
 */
const cache = new Map<string, { symbol: string; name: string }>()

export interface TokenIdentity {
  /** Bare ticker, e.g. "STARO". */
  symbol: string
  /** Rendered ticker, e.g. "$STARO" — what a player sees. */
  ticker: string
  /** Long form, e.g. "Staro Token". */
  name: string
}

/**
 * The tenant's token name, for labels.
 *
 * Never used in a request or an amount — it is display only, so a stale or
 * fallback value can mislabel a screen but can never mis-address an operation.
 */
export const useTokenIdentity = (tenant: string): TokenIdentity => {
  const [identity, setIdentity] = useState(
    () => cache.get(tenant) ?? { symbol: FALLBACK_SYMBOL, name: "Token" }
  )

  useEffect(() => {
    const cached = cache.get(tenant)
    if (cached) {
      setIdentity(cached)
      return
    }

    let cancelled = false
    void AdminTokenService.getConfig(tenant).then((res) => {
      // A failure is not surfaced: this only decides a label, and an operator
      // being shown "$TOKEN" is better than an error banner on a working page.
      if (cancelled || !res.ok || !res.data) return
      const next = {
        symbol: res.data.token_symbol?.trim() || FALLBACK_SYMBOL,
        name: res.data.token_name?.trim() || "Token"
      }
      cache.set(tenant, next)
      setIdentity(next)
    })

    return () => {
      cancelled = true
    }
  }, [tenant])

  return { ...identity, ticker: `$${identity.symbol}` }
}
